import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import PALETTE from 'react/shared/theme/palette';
import api from 'react/signups/card/api';
import Failure from 'react/signups/card/modules/review/failure/Failure';
import ReviewOrder from 'react/signups/card/modules/review/review_order/ReviewOrder';
import Success from 'react/signups/card/modules/review/success/Success';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import { goToNextStep } from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import cardAccountTrustShape from 'react/signups/card/shapes/CardAccountTrust';
import cardProgramQuestionsShape from 'react/signups/card/shapes/CardProgramQuestions';
import organizationShape from 'react/signups/card/shapes/Organization';
import recipeShape from 'react/signups/card/shapes/Recipe';
import userShape from 'react/signups/card/shapes/User';

const useStyles = makeStyles({
  splashBackground: {
    background: PALETTE.white,
    position: 'absolute',
    zIndex: '-1',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    minHeight: '820px',
  },
});

export default function Review({
  account,
  cardAccountSignup,
  cardAccountTrust,
  cardProgramQuestions,
  organization,
  recipe,
  setAccount,
  setOrganization,
  user,
}) {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const userType = cardAccountSignup.userType;

  // State hooks ------------------------------------------------

  const [submitting, setSubmitting] = useState(false);
  const [moduleComplete, setModuleComplete] = useState(false);

  const [reviewData, setReviewData] = useState({
    agreements: [],
  });

  const shouldVerifyIdentity =
    userType === USER_TYPES.CLIENT
      ? !(
          cardAccountTrust.trusteeCategory === 'Bank or credit union' ||
          cardAccountTrust.trusteeCategory === 'Trust company'
        )
      : true;

  // Effect hooks and memoized callbacks ------------------------

  // Move to next step
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      moduleComplete,
      setModuleComplete,
      recipe,
      userType,
    });
  }, [location, navigate, recipe, userType, cardAccountSignup, moduleComplete, setModuleComplete]);

  // Redirect to dashboard after CardAccountSignup marked completed
  const onComplete = useCallback(() => {
    window.location.href = RailsRoutes.user_home_path();
  }, []);

  // navigate to next step when Module-level state changes in response to form submit
  useUpdateEffect(() => {
    const agreements = reviewData.agreements;

    setSubmitting(true);
    Promise.all([
      api.updateAccount(account.id, {
        card_order: {
          i_agree_to_the_esign_agreement: agreements.includes('esignAgreement'),
          i_agree_to_the_master_services_agreement: agreements.includes('msaAgreement'),
          i_am_authorized_by_cardholder: agreements.includes('cardholderAuthorized'),
        },
      }),
      api.completeSignup(cardAccountSignup.id, window.sardineConfig?.sessionKey),
    ])
      .then(() => {
        setSubmitting(false);
        onNavigate();
      })
      .catch(() => {
        setSubmitting(false);
      });
  }, [reviewData]);

  // Pass-down state, props and handlers ------------------------------

  const passDownValues = {
    account,
    cardProgramQuestions,
    organization,
    firstName: account.cardholder?.firstName || '',
    lastName: account.cardholder?.lastName || '',
    reviewData,
    submitting,
    user,
    userType: cardAccountSignup.userType,
  };

  const passDownHandlers = {
    onComplete,
    setAccount,
    setReviewData,
    setOrganization,
  };

  const reviewOrder = <ReviewOrder {...passDownValues} {...passDownHandlers} />;
  const success = (
    <>
      <div className={classes.splashBackground} />
      {$tlf?.flipperEnableTrustComplianceQuestionnaire && (
        <>
          {(userType === USER_TYPES.MYSELF && !user.idvPassed) ||
          (userType !== USER_TYPES.MYSELF &&
            account.fundedByTrust &&
            shouldVerifyIdentity &&
            !user.idvPassed) ? (
            <Failure {...passDownValues} {...passDownHandlers} />
          ) : (
            <Success {...passDownValues} {...passDownHandlers} />
          )}
        </>
      )}
      {!$tlf?.flipperEnableTrustComplianceQuestionnaire && (
        <>
          {userType === USER_TYPES.MYSELF || !user.idvPassed ? (
            <Failure {...passDownValues} {...passDownHandlers} />
          ) : (
            <Success {...passDownValues} {...passDownHandlers} />
          )}
        </>
      )}
    </>
  );

  return (
    <Routes>
      <Route element={reviewOrder} path="/review_order" />
      <Route element={success} path="/success" />
      <Route element={<Navigate replace to={recipe.defaultPath} />} path="/*" />
    </Routes>
  );
}

Review.propTypes = {
  account: PropTypes.object,
  cardAccountSignup: cardAccountSignupShape,
  cardAccountTrust: cardAccountTrustShape,
  cardProgramQuestions: cardProgramQuestionsShape,
  organization: organizationShape.isRequired,
  recipe: recipeShape.isRequired,
  setAccount: PropTypes.func.isRequired,
  setOrganization: PropTypes.func.isRequired,
  user: userShape,
};
